import { Fade, useTheme } from '@mui/material';
import { portalPaths, UrlSegments } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-querybuilder';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { Client, RecordOpenMode } from 'src/graphql';
import { useRecordOpenMode } from 'src/graphql/entities/record-open-mode/record-open-mode';
import { EntityNames } from 'src/graphql/types';
import { ClientSidePanel } from 'src/pages/Clients/SidePanel/side-panel';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';
import { useSelectedEntityParams } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-entity-params';
import { TabType, useCustomView, useGlobalTabs, useOnEsc } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { Platform, useScreenDimensions } from 'src/utils/system';

export const ClientSelectedView = () => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const [selectedRecord, setSelectedRecord] = useState<Client | undefined>();
  const theme = useTheme();

  const customViewId = useCustomView(EntityNames.Client)?.customViewId;

  const { openInTab } = useGlobalTabs();

  const {
    record: client,
    recordId: clientId,
    openRecord,
    closeRecord,
    customView,
    viewId,
    selectedCustomViewId,
  } = useSelectedEntityParams({ entityName: EntityNames.Client });

  const selectedClient = useMemo(
    () => (clientId ? ((client ?? selectedRecord) as Client) : undefined),
    [clientId, client, selectedRecord],
  );

  const prevClient = usePrevious(client);

  const onPanelClose = useCallback(() => {
    closeRecord();
    setSelectedRecord(undefined);
  }, [closeRecord]);

  useEffect(() => {
    // close panel if client was deleted
    if (selectedRecord && prevClient && prevClient?.id === selectedRecord.id && !client) {
      onPanelClose();
    }
  }, [client, selectedRecord, onPanelClose, prevClient]);

  useOnEsc(onPanelClose);

  const { currentOpenMode } = useRecordOpenMode();

  const onRecordClick = useCallback(
    (clientId: string, client: Client, event?: React.MouseEvent) => {
      if (Platform.isMacOS ? event?.metaKey : event?.ctrlKey) {
        openInTab({ entity: client, type: TabType.Client }, { customViewId }, false);
        return;
      } else {
        if (!isMobile && currentOpenMode === RecordOpenMode.FullPage) {
          navigate(portalPaths.client.full(clientId), { state: { customViewId } });
        } else {
          openRecord(client ? client.clientId : clientId);
        }
      }

      setSelectedRecord(client);
    },
    [currentOpenMode, customViewId, isMobile, navigate, openInTab, openRecord],
  );

  return (
    <MetaTags title={selectedClient?.fullName || customView?.name || 'Clients'}>
      <BaseSelectedView<Client>
        title={selectedCustomViewId === UrlSegments.view.all ? 'All clients' : ''}
        entity={EntityNames.Client}
        viewId={viewId}
        selectedRecord={selectedClient}
        onRecordClick={onRecordClick}
        onRecordClose={() => {
          closeRecord();
          setSelectedRecord(undefined);
        }}
        leftPanelWidth={5000}
        sx={{
          [theme.breakpoints.up(0)]: {
            maxWidth: '100%',
          },
        }}
        slots={{
          rightPanel: !!selectedClient && (
            <Fade in={!!selectedClient}>
              <EntityWindowWrapper
                sx={{
                  [theme.breakpoints.up('sm')]: {
                    minWidth: '340px',
                  },
                }}
              >
                <ClientSidePanel onClose={onPanelClose} clientId={selectedClient.clientId} />
              </EntityWindowWrapper>
            </Fade>
          ),
          createButton: <CreateButton icon="person_add" />,
        }}
      />
    </MetaTags>
  );
};
